import React, { useMemo } from 'react';
import { formatNumber, formatDifference, formatRank, formatPercentile } from '../../utils/formatting';
import { getValueColor, getRateColor, getRateDiffColor } from '../../utils/coloring';
import { getStateAbbreviation } from '../../constants/locations';
import './Tables.css';

const LocationNameRatesTable = ({ selectedLocation, selectedCrime, selectedSource, compareMode }) => {
  const current = selectedLocation?.data?.current;
  const locationType = selectedLocation.info?.type?.charAt(0).toUpperCase() + selectedLocation.info?.type?.slice(1);
  const isNation = selectedLocation.info?.type?.toLowerCase() === 'nation';
  const isCounty = selectedLocation.info?.type?.toLowerCase() === 'county';
  const isCity = selectedLocation.info?.type?.toLowerCase() === 'city';
  const isMetro = selectedLocation.info?.type?.toLowerCase() === 'metro';
  const isCountyOrCity = isCounty || isCity;
  
  const stateAbbr = useMemo(() => {
    if (isCountyOrCity && selectedLocation?.info?.name?.includes(',')) {
      const stateName = selectedLocation.info.name.split(',')[1]?.trim();
      return getStateAbbreviation(stateName);
    }
    return getStateAbbreviation(selectedLocation);
  }, [isCountyOrCity, selectedLocation]);
  
  const isMobile = window.innerWidth <= 767;

  if (selectedLocation?.loading || !selectedLocation?.data?.current || selectedLocation?.info?.name?.startsWith('All ')) {
    return null;
  }

  const hasCrimeData = [
    'all_crime_count', 'violent_crime_count', 'property_crime_count',
    'murder_count', 'rape_count', 'robbery_count', 'aggravated_assault_count',
    'burglary_count', 'larceny_theft_count', 'motor_vehicle_theft_count'
  ].some(field => 
    current[field] !== null && 
    current[field] !== undefined && 
    current[field] !== 'N/A'
  );
  
  if (!hasCrimeData) {
    return <div className="loading">No Data Available</div>;
  }

  const getComparisonLabel = () => {
    if (isMetro) {
      return compareMode.rateType === 'metro' ? 'Metro' : 'National';
    }
    if (compareMode.rateType === 'county' || compareMode.rateType === 'city') {
      const locationTypeLabel = compareMode.rateType === 'county' ? 'County' : 'City';
      return compareMode.rateScope === 'state'
        ? `${stateAbbr} ${locationTypeLabel}`
        : `US ${locationTypeLabel}`;
    } else { 
      return compareMode.rateScope === 'state' ? `${stateAbbr} State` : 'US National';
    }
  };

  const transformData = () => {
    const isNCVS = selectedSource === 'BJS_NCVS';
  
    const getComparisonFields = (prefix) => {
      let avgRateField, rankField, rateDiffField, rateDiffPctField, percentileField;
  
      if (isMetro) {
        rankField = `${prefix}_national_rate_rank`;
        percentileField = `${prefix}_national_rate_percentile`;
        if (compareMode.rateType === 'metro') {
          avgRateField = `${prefix}_all_metros_rate`;
          rateDiffField = `${prefix}_all_metros_rate_diff`;
          rateDiffPctField = `${prefix}_all_metros_rate_diff_pct`;
        } else {
          avgRateField = `${prefix}_national_rate`;
          rateDiffField = `${prefix}_national_rate_diff`;
          rateDiffPctField = `${prefix}_national_rate_diff_pct`;
        }
      } else if (isCountyOrCity) {
        if (compareMode.rateType === 'county' || compareMode.rateType === 'city') {
          const locationTypePlural = compareMode.rateType === 'county' ? 'counties' : 'cities';
          if (compareMode.rateScope === 'state') {
            rankField = `${prefix}_state_rate_rank`;
            percentileField = `${prefix}_state_rate_percentile`;
            avgRateField = `${prefix}_all_${locationTypePlural}_state_rate`;
            rateDiffField = `${prefix}_all_${locationTypePlural}_state_rate_diff`;
            rateDiffPctField = `${prefix}_all_${locationTypePlural}_state_rate_diff_pct`;
          } else { 
            rankField = `${prefix}_national_rate_rank`;
            percentileField = `${prefix}_national_rate_percentile`;
            avgRateField = `${prefix}_all_${locationTypePlural}_national_rate`;
            rateDiffField = `${prefix}_all_${locationTypePlural}_national_rate_diff`;
            rateDiffPctField = `${prefix}_all_${locationTypePlural}_national_rate_diff_pct`;
          }
        } else { 
          if (compareMode.rateScope === 'state') {
            rankField = `${prefix}_state_rate_rank`;
            percentileField = `${prefix}_state_rate_percentile`;
            avgRateField = `${prefix}_state_rate`;
            rateDiffField = `${prefix}_state_rate_diff`;
            rateDiffPctField = `${prefix}_state_rate_diff_pct`;
          } else { 
            rankField = `${prefix}_national_rate_rank`;
            percentileField = `${prefix}_national_rate_percentile`;
            avgRateField = `${prefix}_national_rate`;
            rateDiffField = `${prefix}_national_rate_diff`;
            rateDiffPctField = `${prefix}_national_rate_diff_pct`;
          }
        }
      } else {
        rankField = `${prefix}_rate_rank`;
        percentileField = `${prefix}_rate_percentile`;
        avgRateField = `${prefix}_national_rate`;
        rateDiffField = `${prefix}_national_rate_diff`;
        rateDiffPctField = `${prefix}_national_rate_diff_pct`;
      }
      
      return {
        comparisonRate: current[avgRateField],
        difference: current[rateDiffField],
        percentDiff: current[rateDiffPctField],
        rank: current[rankField],
        percentile: current[percentileField]
      };
    };
  
    const allCrimes = [
      {
        crimeType: 'All Crime',
        level: 0,
        rate: current.all_crime_rate,
        ...getComparisonFields('all_crime')
      },
      {
        crimeType: 'Violent Crime',
        level: 1,
        rate: current.violent_crime_rate,
        ...getComparisonFields('violent_crime')
      },
      {
        crimeType: 'Murder',
        level: 2,
        rate: current.murder_rate,
        ...getComparisonFields('murder')
      },
      {
        crimeType: 'Rape',
        level: 2,
        rate: current.rape_rate,
        ...getComparisonFields('rape')
      },
      {
        crimeType: 'Robbery',
        level: 2,
        rate: current.robbery_rate,
        ...getComparisonFields('robbery')
      },
      {
        crimeType: 'Aggravated Assault',
        level: 2,
        rate: current.aggravated_assault_rate,
        ...getComparisonFields('aggravated_assault')
      },
      ...(isNCVS ? [{
        crimeType: 'Simple Assault',
        level: 2,
        rate: current.simple_assault_rate,
      }] : []),
      {
        crimeType: 'Property Crime',
        level: 1,
        rate: current.property_crime_rate,
        ...getComparisonFields('property_crime')
      },
      {
        crimeType: 'Burglary',
        level: 2,
        rate: current.burglary_rate,
        ...getComparisonFields('burglary')
      },
      ...(isNCVS ? [{
        crimeType: 'Trespassing',
        level: 2,
        rate: current.trespassing_rate,
      }] : []),
      {
        crimeType: 'Larceny/Theft',
        level: 2,
        rate: current.larceny_theft_rate,
        ...getComparisonFields('larceny_theft')
      },
      {
        crimeType: 'Motor Vehicle Theft',
        level: 2,
        rate: current.motor_vehicle_theft_rate,
        ...getComparisonFields('motor_vehicle_theft')
      }
    ];
  
    return allCrimes;
  };

  const renderRow = (item) => {
    const isSelected = item.crimeType.toLowerCase().replace(/[\s/]/g, '_') === selectedCrime?.toLowerCase();

    return (
      <tr key={item.crimeType} className={`level-${item.level}${isSelected ? ' selected-row' : ''}`}>
        <td className="crime-type-cell">
          <div className="crime-type-content">
            <span>{item.crimeType}</span>
          </div>
        </td>
        <td className={`number-cell ${isNation ? 'nation' : ''}`} style={{ color: getRateColor(item.rate, item.comparisonRate) }}>
          {formatNumber(item.rate)}
        </td>
        {!isNation && (
          <>
            <td className="number-cell">{formatNumber(item.comparisonRate)}</td>
            <td className="number-cell bold" style={{ color: getRateDiffColor(item.difference) }}>
              {formatDifference(item.difference, false, true)}
            </td>
            <td className="number-cell" style={{ color: getValueColor(item.percentDiff) }}>
              {formatDifference(item.percentDiff, true)}
            </td>
            <td className="rank-cell">{formatRank(item.rank)}</td>
            <td className="rank-cell">{formatPercentile(item.percentile)}</td>
          </>
        )}
      </tr>
    );
  };

  const data = transformData();

  return (
    <div className="main-table-wrapper">
      <div className="main-table-top">
        {isNation && selectedSource === 'BJS_NCVS' ? (
          <>
            <div style={{ fontSize: isMobile ? '10px' : '14px' }}>US Residents Age 12 or Older:&nbsp;<strong>{formatNumber(current.person_population)}</strong></div>
            <div style={{ fontSize: isMobile ? '12px' : '14px' }}>{isMobile ? "\u00A0|\u00A0" : "\u00A0\u00A0|\u00A0\u00A0"}</div>
            <div style={{ fontSize: isMobile ? '10px' : '14px' }}>US Households:&nbsp;<strong>{formatNumber(current.household_population)}</strong></div>
          </>
        ) : (
          <>
            {isNation ? "United States" : locationType} Population:&nbsp;<strong>{formatNumber(current.population)}</strong>
          </>
        )}
      </div>
      <div className="main-table-scroll-container location-name-table">
        <table className={`main-table location-name-table ${isNation ? 'nation-view' : ''}`}>
          <thead>
            <tr>
              <th>Type of Crime</th>
              <th>
                {isNation ? (
                  <div className="single-line-header">
                    {selectedSource === 'BJS_NCVS' ? 'Victimization Rate' : 'Crime Rate'}
                  </div>
                ) : (
                  <div className="stacked-header">
                    <div>Crime</div>
                    <div>Rate</div>
                  </div>
                )}
              </th>
              {!isNation && (
                <>
                  <th>
                    <div className="stacked-header">
                      <div>{(isCountyOrCity || isMetro) ? getComparisonLabel() : 'National'}</div>
                      <div>Rate</div>
                    </div>
                  </th>
                  <th>
                    <div className="stacked-header">
                      <div>{(isCountyOrCity || isMetro) ? getComparisonLabel() : 'National'} Rate</div>
                      <div>Difference</div>
                    </div>
                  </th>
                  <th>
                    <div className="stacked-header">
                      <div>% {(isCountyOrCity || isMetro) ? getComparisonLabel() : 'National'} Rate</div>
                      <div>Difference</div>
                    </div>
                  </th>
                  <th>
                    <div className="stacked-header">
                      <div>{isCountyOrCity ? getComparisonLabel() : locationType}</div>
                      <div>Rank</div>
                    </div>
                  </th>
                  <th>
                    <div className="stacked-header">
                      <div>{isCountyOrCity ? getComparisonLabel() : locationType}</div>
                      <div>Percentile</div>
                    </div>
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {data.map(item => renderRow(item))}
          </tbody>
        </table>
      </div>
    </div>
  );

};

export default LocationNameRatesTable;